import React, { useRef, useEffect } from 'react';

/**
 * RichTextEditor — a minimal WYSIWYG for the event description, drawn as the DESCRIPTION
 * row of the host event edit handoff: a grey panel (radius 22) with a toolbar of white
 * pills above the editable body. Supports headings, paragraphs, bold/italic, lists and
 * links on selected URLs. Deliberately offers NO font/size/color controls, and the server
 * sanitizes the HTML on save (stripping any style attributes), so the typeface can never
 * be changed. Outputs HTML via onChange. Renders inside the wizard's `.hev` scope.
 *
 * `error` only paints the red border — the page's Field prints the message.
 */
const URL_LIKE = /^(https?:\/\/|www\.)\S+$/i;

export default function RichTextEditor({ value, onChange, onBlur, disabled, placeholder, error, bare = false }) {
  const ref = useRef(null);

  // New lines are paragraphs, not the browser's default <div>s (the server maps those too,
  // but writing <p> keeps the stored HTML and the editor's own DOM the same shape).
  useEffect(() => {
    try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch { /* jsdom / old engines */ }
  }, []);

  // Sync the DOM when the value changes from outside (e.g. edit-mode load).
  // We never write innerHTML on every keystroke — that would reset the caret.
  useEffect(() => {
    if (ref.current && (value || '') !== ref.current.innerHTML) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const emit = () => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    // Treat an "empty" editor (just <br> / blank block) as truly empty.
    onChange?.(/^(<br>|<div><br><\/div>|<p><br><\/p>|\s)*$/i.test(html) ? '' : html);
  };

  const exec = (command, arg) => {
    if (disabled) return;
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  // LINK is prompt-free: the selected text has to be the URL. Anything else is a no-op.
  const link = () => {
    if (disabled) return;
    const text = (window.getSelection?.()?.toString() || '').trim();
    if (!URL_LIKE.test(text)) return;
    const url = /^www\./i.test(text) ? `https://${text}` : text;
    exec('createLink', url);
  };

  const Tool = ({ label, title, weight = 600, italic = false, onClick }) => (
    <button
      type="button"
      className="hev-rte-btn"
      title={title}
      aria-label={title}
      disabled={disabled}
      style={{ fontWeight: weight, fontStyle: italic ? 'italic' : 'normal' }}
      // mousedown + preventDefault keeps the editor's selection from collapsing
      // when the toolbar button takes focus.
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    >
      {label}
    </button>
  );

  return (
    <div className="hev-rte" data-error={error ? '' : undefined} data-bare={bare ? '' : undefined}>
      <div className="hev-rte-bar" role="toolbar" aria-label="Formatting">
        <Tool label="¶" title="Normal text" onClick={() => exec('formatBlock', '<p>')} />
        <Tool label="H" title="Heading" weight={800} onClick={() => exec('formatBlock', '<h2>')} />
        <Tool label="B" title="Bold" weight={800} onClick={() => exec('bold')} />
        <Tool label="I" title="Italic" weight={500} italic onClick={() => exec('italic')} />
        <Tool label="• LIST" title="Bulleted list" onClick={() => exec('insertUnorderedList')} />
        <Tool label="1. LIST" title="Numbered list" onClick={() => exec('insertOrderedList')} />
        <Tool label="LINK" title="Link (select a URL first)" onClick={link} />
      </div>
      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-disabled={disabled || undefined}
        data-placeholder={placeholder || 'Describe your event…'}
        onInput={emit}
        onBlur={onBlur}
        className="hev-rte-body prose-event"
        style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
      />
    </div>
  );
}
